"use client"

import { useState } from "react"
import PropTypes from "prop-types"
import { getSettings, setSettings } from "../../utils"
import Words from "../components/words"
import Progress from "../components/progress"
import { useLanguage } from "../../../../../contexts/LanguageContext"


const difficulties = [
  [
    { name: "multiDifficulties/multiplicationTable" },
    { name: "multiDifficulties/2x1" },
    { name: "multiDifficulties/3x1" },
    { name: "multiDifficulties/2x2" },
    { name: "multiDifficulties/4x1" },
    { name: "multiDifficulties/3x2" },
    { name: "multiDifficulties/base50" },
    { name: "multiDifficulties/base100" },
    { name: "multiDifficulties/base200" },
  ],
  [
    { name: "multiDifficulties/byMultiplicationTable" },
    { name: "multiDifficulties/2div1" },
    { name: "multiDifficulties/3div1" },
    { name: "multiDifficulties/4div1" },
    { name: "multiDifficulties/2div2" },
    { name: "multiDifficulties/3div2" },
    { name: "multiDifficulties/4div2" },
    { name: "multiDifficulties/4div3" },
  ],
  [
    { name: "multiDifficulties/2-10" },
    { name: "multiDifficulties/on5" },
    { name: "multiDifficulties/11-19" },
    { name: "multiDifficulties/20-30" },
    { name: "multiDifficulties/30-70" },
    { name: "multiDifficulties/80-120" },
    { name: "any" },
  ],
]

const multipliers = [
  [
    [
      { name: "2" },
      { name: "3" },
      { name: "4" },
      { name: "5" },
      { name: "6" },
      { name: "7" },
      { name: "8" },
      { name: "9" },
      { name: "1-9" },
    ],
    [
      { name: "2" },
      { name: "3" },
      { name: "4" },
      { name: "5" },
      { name: "6" },
      { name: "7" },
      { name: "8" },
      { name: "9" },
      { name: "1-9" },
    ],
    [
      { name: "2" },
      { name: "3" },
      { name: "4" },
      { name: "5" },
      { name: "6" },
      { name: "7" },
      { name: "8" },
      { name: "9" },
      { name: "1-9" },
    ],
    [
      { name: "11" },
      { name: "12" },
      { name: "13" },
      { name: "14" },
      { name: "15" },
      { name: "16" },
      { name: "17" },
      { name: "18" },
      { name: "19" },
      { name: "20-29" },
      { name: "30-39" },
      { name: "40-49" },
      { name: "50-59" },
      { name: "60-69" },
      { name: "70-79" },
      { name: "80-89" },
      { name: "90-99" },
      { name: "any" },
    ],
    [
      { name: "2" },
      { name: "3" },
      { name: "4" },
      { name: "5" },
      { name: "6" },
      { name: "7" },
      { name: "8" },
      { name: "9" },
      { name: "1-9" },
    ],
    [{ name: "any" }],
    [{ name: "50-60" }, { name: "40-50" }, { name: "50-60 x 40-50" }],
    [{ name: "100-110" }, { name: "90-100" }, { name: "100-110 x 90-100" }],
    [{ name: "190-200" }, { name: "200-210" }, { name: "190-200 x 200-210" }],
  ],
  [
    [
      { name: "2" },
      { name: "3" },
      { name: "4" },
      { name: "5" },
      { name: "6" },
      { name: "7" },
      { name: "8" },
      { name: "9" },
      { name: "1-9" },
    ],
    [
      { name: "2" },
      { name: "3" },
      { name: "4" },
      { name: "5" },
      { name: "6" },
      { name: "7" },
      { name: "8" },
      { name: "9" },
      { name: "1-9" },
    ],
    [
      { name: "2" },
      { name: "3" },
      { name: "4" },
      { name: "5" },
      { name: "6" },
      { name: "7" },
      { name: "8" },
      { name: "9" },
      { name: "1-9" },
    ],
    [
      { name: "2" },
      { name: "3" },
      { name: "4" },
      { name: "5" },
      { name: "6" },
      { name: "7" },
      { name: "8" },
      { name: "9" },
      { name: "1-9" },
    ],
    [{ name: "any" }],
    [{ name: "any" }],
    [{ name: "any" }],
    [{ name: "any" }],
  ],
]

const TrainingSettings = ({ onStart }) => {
  const { t } = useLanguage()
  const [settings, setSettingsState] = useState(getSettings("multi"))

  const changeSettings = (name, value) => {
    const newSettings = { ...setSettings(name, value).multi }
    setSettingsState(newSettings)
  }

  const getThemeNames = () => [
    { name: t("multiThemes/multiplication") },
    { name: t("multiThemes/division") },
    { name: t("multiThemes/squaring") },
  ]

  return (
    <div>
      <h2>{t("selectMultiSettings")}</h2>
      <section>
        <Words
          words={getThemeNames()}
          currentVal={settings.theme}
          description={t("theme")}
          onChange={(val) => {
            changeSettings(`multi.theme`, val)
          }}
        />
        {settings.theme < 3 && (
          <Words
            words={difficulties[settings.theme].map((d) => ({ name: t(d.name) }))}
            currentVal={settings.difficulties[settings.theme]}
            description={t("difficulty")}
            onChange={(val) => {
              changeSettings(`multi.difficulties[${settings.theme}]`, val)
              changeSettings(`multi.num`, 0)
            }}
          />
        )}
        {settings.theme < 2 && (
          <Words
            words={multipliers[settings.theme][settings.difficulties[settings.theme]]}
            currentVal={settings.num}
            description={settings.theme === 0 ? t("multiplier") : settings.theme === 1 ? t("divisor") : t("multiplier")}
            onChange={(val) => {
              changeSettings(`multi.num`, val)
            }}
          />
        )}
        <Progress
          minimum={10}
          maximum={60}
          step={1}
          currentVal={settings.time}
          description={`${t("inputTime")} ${settings.time} ${t("sec")}`}
          onChange={(val) => {
            changeSettings("multi.time", val)
          }}
        />
      </section>
      <footer>
        <button onClick={() => onStart()}>
          <svg fill="#FFF" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path strokeWidth={2} 
              d="M 4.1565441,2.8916291 20.245369,12.180516 4.1084691,21.136604 Z"
            />
          </svg>
          <div>{t("start")}</div>
        </button>
      </footer>
    </div>
  )
}

TrainingSettings.propTypes = {
  onStart: PropTypes.func.isRequired,
}

export default TrainingSettings
